/**
 * TypeScript Type Definitions for VON (Vermont Oxford Network) Data
 * 14 Sections covering comprehensive neonatal data collection
 * Last updated: 2025-11-10
 */

// ============================================================================
// SECTION 00: Perinatal - General Information
// ============================================================================
export interface Von00Perinatal {
  id?: number;
  patient_id: number;

  // General information
  sex?: number; // 1=M, 2=F, 9=Unknown
  ga_days?: number; // Gestational age - days (0-6)
  ga_weeks?: number; // Gestational age - weeks (20-44)
  birthweight_g?: number; // Birth weight in grams (300-6000)
  head_circumference?: number; // Head circumference at birth (cm)
  died_first_12hours?: boolean; // Died within 12 hours

  // Auto-fill tracking
  _autofilled?: string[]; // Array of field names that were auto-filled from patient data

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 01: Maternal & Perinatal Information
// ============================================================================
export interface Von01Maternal {
  id?: number;
  patient_id: number;

  // Maternal identification
  mother_name?: string | null; // Read-only, from patient data
  maternal_id?: string; // Maternal CNP
  mrn?: string; // ID foaie observatie (MRN)
  birth_datetime?: string; // Birth date & time
  admit_datetime?: string; // Admission date & time to NICU
  died_dr?: boolean; // Died in delivery room

  // Site information
  site_id?: number; // Hospital/Site ID
  inborn?: boolean; // Born in your hospital
  transfer_from?: string; // Transferred from center
  transfer_to?: string; // Transferred to center
  transfer_from_details?: string; // Details when transferred from abroad
  transfer_to_details?: string; // Details when transferred to abroad
  transfer_from_reason?: number; // 1=ECMO, 3=Medical, 4=Surgery, 5=Chronic, 6=Other, 7=Hypothermia, 8=CRRT

  // Prenatal care
  antenatal_steroids_qual?: number; // 1=Complete, 2=Partial, 3=No, 9=Unknown
  prenatal_care?: boolean; // Prenatal care received
  mode_delivery?: number; // 1=Vaginal, 2=Cesarean

  // Maternal conditions
  maternal_htn?: boolean; // Maternal hypertension
  maternal_diabetes?: boolean; // Maternal diabetes
  chorio?: boolean; // Chorioamnionitis
  ant_magnesium?: boolean; // Antenatal magnesium sulfate
  maternal_lupus?: boolean; // Maternal lupus
  maternal_conditions_other?: string; // Other maternal conditions

  // Maternal age, exposures and folate
  maternal_age?: number; // Maternal age (years)
  exposure_smoking?: boolean; // Smoking
  exposure_drugs?: boolean; // Drugs
  exposure_teratogenic_meds?: boolean; // Teratogenic medication (no longer in the form)
  exposure_infection?: boolean; // Infection
  exposures_other?: string; // Other exposures
  folate_supplementation?: number; // 1=Yes, 2=No, 3=Unknown

  // Multiple gestation
  maternal_gest?: boolean; // Multiple gestation
  multiple_count?: number; // 1=Two, 2=Three, 3=Four

  // Auto-fill tracking
  _autofilled?: string[]; // Array of field names that were auto-filled from patient data

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 02: Birth & Resuscitation
// ============================================================================
export interface Von02Resuscitation {
  id?: number;
  patient_id: number;

  // APGAR scores
  apgar_1?: number; // APGAR at 1 min (0-10)
  apgar_5?: number; // APGAR at 5 min (0-10)

  // Resuscitation details
  initial_resus?: boolean; // Initial resuscitation performed
  resus_oxygen?: boolean; // Oxygen in delivery room
  resus_face_mask?: boolean; // Face mask ventilation
  resus_supraglottic?: boolean; // Supraglottic airway device
  resus_et_tube?: boolean; // Endotracheal tube ventilation
  resus_epinephrine?: boolean; // Epinephrine (DR)
  resus_compressions?: boolean; // Cardiac compressions (DR)
  resus_nasal_vent?: boolean; // Nasal ventilation
  resus_nasal_cpap?: boolean; // Nasal CPAP

  // Temperature
  resus_temperature?: number; // 0=No, 1=Yes, 9=N/A
  measured_temp?: number; // Measured temperature in Celsius

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 03: Respiratory Support
// ============================================================================
export interface Von03RespSupport {
  id?: number;
  patient_id: number;

  // Surfactant
  dr_surfactant?: boolean; // Surfactant during initial resuscitation
  rds_surf_given?: boolean; // Surfactant given at any time
  rds_surf_dose1_mgkg?: number; // First surfactant dose (mg/kg)
  rds_first_surf_age_h?: number; // Age at first dose - hours
  rds_first_surf_age_min?: number; // Age at first dose - minutes

  // Respiratory support after initial resuscitation
  ox_after_initial_resus?: boolean; // Oxygen after initial resus
  vent_after_initial_resus?: boolean; // Conventional ventilation after initial resus
  hfv_after_initial_resus?: boolean; // High frequency ventilation after initial resus
  ncf_?: boolean; // Nasal canula flow
  ncf_after_initial_resus?: boolean; // NC flow >2 L/min after initial resus
  nimv_after_initial_resus?: boolean; // Nasal ventilation after initial resus
  cpap_after_initial_resus?: boolean; // Nasal CPAP after initial resus

  // Inhaled nitric oxide
  ino?: boolean; // Inhaled nitric oxide
  ino_given?: number; // 1=Local NICU, 2=Transferring NICU, 3=Both

  // Conditions
  rds?: boolean; // Respiratory distress syndrome
  ptx?: boolean; // Pneumothorax
  ptx_where?: number; // 1=Local NICU, 2=Other hospital, 3=Both
  chronic_lung_disease?: boolean; // Chronic lung disease
  resp_other?: string; // Other respiratory conditions
  ox_day28?: boolean; // Oxygen on day 28

  // Additional treatments
  caffeine_any?: boolean; // Caffeine for any reason
  vita_im?: boolean; // Intramuscular Vitamin A

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 04: Respiratory Course - Oxygen at 36 weeks
// ============================================================================
export interface Von04RespCourse {
  id?: number;
  patient_id: number;

  // Support at 36 weeks
  ox36?: number; // Oxygen at 36 weeks (0=No, 1=Yes, 9=N/A)
  vent36?: number; // Conventional ventilation at 36 weeks
  hfv36?: number; // HFV at 36 weeks
  nazal_canule?: boolean; // Nasal canula flow
  ncf36?: number; // NC flow >2 L/min at 36 weeks
  nimv36?: number; // Nasal ventilation at 36 weeks
  cpap36?: number; // Nasal CPAP at 36 weeks

  // Steroids for BPD
  steroids_cld?: boolean; // Steroids for BPD/CLD
  steroids_cld_where?: number; // 1=Your hospital, 2=Other hospital, 3=Both

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 05: Cardiovascular
// ============================================================================
export interface Von05Cardiovascular {
  id?: number;
  patient_id: number;

  // PDA
  pda?: number; // Patent ductus arteriosus (0=No, 1=Yes, 9=N/A)
  ibuprofen_pda?: boolean; // Ibuprofen for PDA
  indomethacin_any?: boolean; // Indomethacin for any reason
  acetamin_pda?: boolean; // Acetaminophen for PDA
  pda_closure_method?: number; // 1=Medical, 2=Interventional cath, 3=Surgical, 4=Spontaneous

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 06: GI & Nutrition
// ============================================================================
export interface Von06GiNutrition {
  id?: number;
  patient_id: number;

  // First feeding
  nutrition_prima_hranire_zi?: string; // First enteral nutrition day

  // NEC
  nec?: boolean; // Necrotizing enterocolitis
  sip?: number; // Focal intestinal perforation (0=No, 1=Surgically confirmed, 2=Clinically diagnosed)
  nec_where?: number; // 1=Local NICU, 2=Transferring NICU, 3=Both

  // Probiotics
  probiotics_any?: boolean; // Probiotics given

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 07: Renal
// ============================================================================
export interface Von07Renal {
  id?: number;
  patient_id: number;

  // Renal conditions
  renal_aki?: boolean; // Acute kidney injury (AKI)
  renal_dialysis?: boolean; // Dialysis performed
  renal_dialysis_type?: number; // 1=Peritoneal, 2=CRRT

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 08: Neurology
// ============================================================================
export interface Von08Neurology {
  id?: number;
  patient_id: number;

  // IVH/PIH
  pih_imaging?: boolean; // Cranial imaging <= day 28
  pih_grade?: number; // Worst PIH grade (0-4)
  pih_where?: number; // 1=Local NICU, 2=Transferring NICU, 3=Both

  // Other neurological conditions
  pvl?: number; // Cystic PVL (0=No, 1=Yes, 9=N/A)
  ventriculitis_any?: boolean; // Ventriculitis
  hydrocephalus_any?: boolean; // Hydrocephalus diagnosed
  seizures?: boolean; // Seizures

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 09: Ophthalmology - ROP
// ============================================================================
export interface Von09Ophthalmology {
  id?: number;
  patient_id: number;

  // ROP examination
  rop_exam?: boolean; // ROP exam performed
  rop_date_diag?: string; // ROP diagnosis date
  rop_eye?: string; // Eye(s) involved: 1=Left, 2=Right, 3=Bilateral
  rop_zone?: number; // ROP Zone (1, 2, 3)
  rop_stage?: number; // Worst ROP stage (0-5)
  rop_plus?: boolean; // Plus disease

  // Anti-VEGF treatment
  rop_anti_vegf?: boolean; // Anti-VEGF treatment given
  rop_anti_vegf_agent?: number; // 1=Bevacizumab, 2=Ranibizumab, 3=Aflibercept, 99=Other
  rop_anti_vegf_date?: string; // Anti-VEGF date
  rop_anti_vegf_where?: number; // 1=Your hospital, 2=Other hospital, 3=Both

  // Laser treatment
  rop_laser?: boolean; // ROP laser treatment
  rop_laser_date?: string; // Laser date
  rop_laser_where?: number; // 1=Your hospital, 2=Other hospital, 3=Both

  // Surgery
  rop_surgery?: boolean; // ROP surgery
  rop_surgery_date?: string; // ROP surgery date
  rop_surgery_type?: number; // 1=Lens-sparing vitrectomy, 2=Scleral buckle, 99=Other
  rop_surgery_where?: number; // 1=Your hospital, 2=Other hospital, 3=Both

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 10: Infections
// ============================================================================
export interface Von09Infections {
  id?: number;
  patient_id: number;

  // Congenital infections
  congen_inf?: boolean; // Congenital infection
  congen_inf_cd1?: string; // Congenital infection code 1
  congen_inf_cd2?: string; // Congenital infection code 2
  congen_inf_cd3?: string; // Congenital infection code 3

  // Early onset sepsis (EOS <= day 3)
  eos_bact_meningitis?: boolean; // Bacterial sepsis/meningitis on/before day 3
  eos_pathogen1?: string; // Pathogen <= day 3 - 1
  eos_pathogen2?: string; // Pathogen <= day 3 - 2
  eos_pathogen3?: string; // Pathogen <= day 3 - 3

  // Late onset sepsis (LOS > day 3)
  los_bact_meningitis?: boolean; // Bacterial sepsis/meningitis after day 3
  los_where?: number; // 1=Your hospital, 2=Outside, 3=Both
  los_pathogen1?: string; // Pathogen > day 3 - 1
  los_pathogen2?: string; // Pathogen > day 3 - 2
  los_pathogen3?: string; // Pathogen > day 3 - 3

  // Specific infections
  cons_after3?: boolean; // Coagulase-negative staph infection after day 3
  cons_after3_where?: number; // 1=Your hospital, 2=Outside, 3=Both
  fungal_after3?: boolean; // Fungal infection after day 3
  fungal_after3_where?: number; // 1=Your hospital, 2=Outside, 3=Both

  // Sepsis pathogen coding
  sepsis_pathogen?: number; // Identified organism (coded 1-18, 99=Other)
  sepsis_pathogen_other?: string; // Other organism (text)

  // Meningitis details
  meningitis_hydro_shunt_date?: string; // Shunt procedure date
  csf_culture_result?: number; // CSF culture result (1=Positive, 2=Negative, 3=Not done)

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 11: Congenital Anomalies
// ============================================================================
export interface Von11Congenital {
  id?: number;
  patient_id: number;

  // Congenital anomalies
  has_congen_anomaly?: boolean | null; // Explicit yes/no; null means unanswered
  congen_anomaly?: string; // Congenital anomaly description
  icd10_q_codes?: string; // ICD-10 Q00-Q99 codes (comma-separated)
  icd10_q_other?: string; // Other ICD-10 Q codes (text)

  // Auto-fill tracking
  _autofilled?: string[]; // Array of field names that were auto-filled from patient data

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 12: Genetic Conditions
// ============================================================================
export interface Von12Genetic {
  id?: number;
  patient_id: number;

  // Genetic testing
  genetic_condition_present?: boolean; // Genetic condition present
  genetic_test_type?: string; // Genetic test type (comma-separated)
  genetic_test_date?: string; // Genetic test date
  genetic_test_result?: number; // 1=Normal, 2=Pathologic, 3=Inconclusive, 9=Unknown

  // Genetic condition details
  genetic_condition_type?: number; // Legacy: 1=Trisomy 21, 2=Trisomy 18, 3=Trisomy 13, etc (read-only display for old records)
  genetic_condition_icd10_code?: string; // ICD-10-AM code (Q90-Q99), replaces genetic_condition_type for new/edited records
  genetic_condition_other?: string; // Other genetic condition (text)

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 13: Readmission
// ============================================================================
export interface Von13Readmission {
  id?: number;
  patient_id: number;

  // Readmission details
  readmission?: boolean; // Readmission after discharge
  readmit_date?: string; // Date of readmission
  readmit_reason?: number; // Reason for readmission (1-10)
  readmit_hosp_id?: number; // Hospital ID where readmitted
  readmit_dispo?: number; // Disposition after readmission (1=Home, 2=Died, 3=Still hospitalized at 1 year)
  readmit_dispo_date?: string; // Date of disposition after readmission

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// SECTION 14: Outcomes & Discharge
// ============================================================================
export interface Von10Outcomes {
  id?: number;
  patient_id: number;

  // Initial disposition
  init_dispo_date?: string; // Date of initial disposition
  still_hosp_center?: boolean; // Still hospitalized at your center

  // Surgeries performed
  rop_surgery?: boolean; // ROP surgery
  pda_surgery?: boolean; // PDA surgery or interventional catheterization
  nec_surgery?: boolean; // Surgery for NEC/suspected NEC/perforation
  other_surgery?: boolean; // Other surgery

  // Surgery details (up to 10 procedures)
  surgery_code_1?: string;
  surgery_loc_1?: number; // 1=Your hospital, 2=Other, 3=Both
  surgery_ssi_1?: boolean; // Surgical site infection

  surgery_code_2?: string;
  surgery_loc_2?: number;
  surgery_ssi_2?: boolean;

  surgery_code_3?: string;
  surgery_loc_3?: number;
  surgery_ssi_3?: boolean;

  surgery_code_4?: string;
  surgery_loc_4?: number;
  surgery_ssi_4?: boolean;

  surgery_code_5?: string;
  surgery_loc_5?: number;
  surgery_ssi_5?: boolean;

  surgery_code_6?: string;
  surgery_loc_6?: number;
  surgery_ssi_6?: boolean;

  surgery_code_7?: string;
  surgery_loc_7?: number;
  surgery_ssi_7?: boolean;

  surgery_code_8?: string;
  surgery_loc_8?: number;
  surgery_ssi_8?: boolean;

  surgery_code_9?: string;
  surgery_loc_9?: number;
  surgery_ssi_9?: boolean;

  surgery_code_10?: string;
  surgery_loc_10?: number;
  surgery_ssi_10?: boolean;

  // Final discharge
  final_discharge?: number; // 1=Home, 2=Died, 3=Transferred, 4=Legacy hospitalized status (no longer offered)
  transfer_reason_out?: number; // Discharge reason (legacy API key): 1=Surgery, 2=Hypothermia, 3=Malnutrition, 4=Diagnostic and treatment services, 5=Chronic care, 6=ECMO, 7=Other
  final_discharge_date?: string; // Date of final discharge
  weight_g_discharge?: number; // Weight at discharge (g)
  days_discharge?: number; // Duration of hospitalization (days)

  // Support at discharge
  ox_discharge?: boolean; // Oxygen at discharge
  vent_discharge?: boolean; // Conventional ventilation at discharge
  hfv_discharge?: boolean; // HFV at discharge
  ncf_discharge?: boolean; // Nasal canula at discharge
  ncf_discharge_gt2l?: boolean; // NC flow >2 L/min at discharge
  nimv_discharge?: boolean; // Nasal ventilation at discharge
  cpap_discharge?: boolean; // Nasal CPAP at discharge
  monitor_discharge?: boolean; // Monitor at discharge
  enteral_feed_discharge?: string; // Enteral feeding at discharge

  // Audit
  recorded_by_user_id?: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// Summary & Aggregate Types
// ============================================================================

export interface VonSummaryCompleteness {
  total_sections: number;
  completed_sections: number;
  percentage: number;
  missing_sections: string[];
}

export interface VonSummary {
  patient_id: number;
  perinatal: Von00Perinatal | null;
  maternal: Von01Maternal | null;
  resuscitation: Von02Resuscitation | null;
  resp_support: Von03RespSupport | null;
  resp_course: Von04RespCourse | null;
  cardiovascular: Von05Cardiovascular | null;
  gi_nutrition: Von06GiNutrition | null;
  renal: Von07Renal | null;
  neurology: Von08Neurology | null;
  ophthalmology: Von09Ophthalmology | null;
  infections: Von09Infections | null;
  congenital: Von11Congenital | null;
  genetic: Von12Genetic | null;
  readmission: Von13Readmission | null;
  outcomes: Von10Outcomes | null;
  completeness: VonSummaryCompleteness;
}

// API Response Types
export interface VonApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface VonSectionName {
  perinatal: string;
  maternal: string;
  resuscitation: string;
  resp_support: string;
  resp_course: string;
  cardiovascular: string;
  gi_nutrition: string;
  renal: string;
  neurology: string;
  ophthalmology: string;
  infections: string;
  congenital: string;
  genetic: string;
  readmission: string;
  outcomes: string;
}

export const VON_SECTION_NAMES: VonSectionName = {
  perinatal: 'Informații generale',
  maternal: 'Istoric matern',
  resuscitation: 'Resuscitare inițială',
  resp_support: 'Suport respirator',
  resp_course: 'Suport la 36 săptămâni',
  cardiovascular: 'Cardiovascular',
  gi_nutrition: 'Digestiv & nutriție',
  renal: 'Afectare renală',
  neurology: 'Neurologie',
  ophthalmology: 'Oftalmologie - ROP',
  infections: 'Infecții',
  congenital: 'Malformații congenitale',
  genetic: 'Afecțiuni genetice',
  readmission: 'Reinternare',
  outcomes: 'Rezultate externare',
};
